// Detección y manejo del flag `nativeApp` del bundle de login. Ver
// QUERYSTRING_BEHAVIOR.md para la semántica completa (precedencia de
// fuentes, persistencia en sessionStorage, política de slots iOS).

import type { KcData } from "./kcData";

// Whitelist de identificadores válidos. Usada también como filtro
// "logo no enlazable" en el header (cualquier valor de la lista
// indica que estamos en una webview de la app móvil → el logo no debe
// llevar al sitio web).
export const VALID_NATIVE_APP_VALUES = [
  "app_android",
  "app_ios",
  "app_android_fd2",
  "app_ios_fd2",
  "app_ios_fd2_a",
  "app_ios_fd2_b",
] as const;

// Apps a las que SÍ se les muestra el botón "Identifícate con Google".
// Las builds iOS rotatorias (app_ios_fd2_a / app_ios_fd2_b) son DOS slots
// para tener simultáneamente una build en tienda y otra en review por
// Apple — cuando Apple rechaza una build por incluir login con Google,
// se quita el slot que toca de esta lista mientras esa build está en
// review. La otra sigue en la lista para los usuarios actuales.
// Procedimiento completo en QUERYSTRING_BEHAVIOR.md § "Política Apple Store".
// NO TOCAR sin entender ese procedimiento.
export const NATIVE_APPS_WITH_SOCIAL_LOGIN = [
  "app_android_fd2",
  "app_ios_fd2",
  "app_ios_fd2_a",
  "app_ios_fd2_b",
] as const;

// Apps que muestran el botón / link de registro. Apple no acepta apps
// iOS_fd2_* con registro habilitado (App Store policy), así que están
// excluidas deliberadamente.
export const NATIVE_APPS_WITH_REGISTER_FEATURE = [
  "app_android",
  "app_ios",
  "app_android_fd2",
] as const;

export type NativeApp = (typeof VALID_NATIVE_APP_VALUES)[number];

export function isIOSNativeApp(nativeApp: string | null): boolean {
  return nativeApp === "app_ios_fd2_a" || nativeApp === "app_ios_fd2_b";
}

function isValid(value: string | null): value is NativeApp {
  return (VALID_NATIVE_APP_VALUES as readonly string[]).includes(value ?? "");
}

// Extrae el `execution=<uuid>` que Keycloak añade a `data.url.loginAction`
// para ligar el sessionStorage["nativeAppDetected"] al flow concreto. Si
// no hay loginAction o no lleva ?execution=, devuelve null.
function findExecutionId(data: KcData): string | null {
  const loginAction = data.url?.loginAction;
  if (!loginAction) return null;
  const qIdx = loginAction.indexOf("?");
  if (qIdx < 0) return null;
  const sp = new URLSearchParams(loginAction.substring(qIdx));
  return sp.get("execution");
}

const SESSION_STORAGE_KEY = "nativeAppDetected";

interface SessionStorageNativeApp {
  nativeApp: NativeApp;
  executionId: string;
}

// Detecta si el flow viene de una app nativa, en este orden de
// precedencia (ver QUERYSTRING_BEHAVIOR.md §1):
//   1. ?nativeApp=<valor> directo en la URL.
//   2. ?external_referrer=<valor> dentro del querystring del redirect_uri
//      (parámetro anidado).
//   3. ?external_referrer=<valor> directo en la URL.
//   4. sessionStorage[nativeAppDetected] (sólo si NO hay redirect_uri y
//      el executionId actual coincide con el guardado).
//
// Sutileza: si llega `redirect_uri`, BORRAMOS el sessionStorage antes de
// seguir. Eso previene que un valor residual de la app móvil contamine
// la sesión cuando el usuario vuelve al navegador.
export function getNativeApp(search: string, data: KcData): NativeApp | null {
  const executionId = findExecutionId(data);
  let hasRedirectUri = false;
  let nativeApp: NativeApp | null = null;

  const sp = new URLSearchParams(search);
  const direct = sp.get("nativeApp");
  if (isValid(direct)) {
    nativeApp = direct;
  } else {
    const redirectUri = sp.get("redirect_uri");
    if (redirectUri) {
      hasRedirectUri = true;
      const innerQuery = redirectUri.indexOf("?");
      if (innerQuery >= 0) {
        const sp2 = new URLSearchParams(redirectUri.substring(innerQuery));
        const fromInner = sp2.get("external_referrer");
        if (isValid(fromInner)) {
          nativeApp = fromInner;
        } else {
          const fromOuter = sp.get("external_referrer");
          if (isValid(fromOuter)) {
            nativeApp = fromOuter;
          }
        }
      }
      try {
        // Borramos un posible valor recordado que pueda venir de una
        // interacción previa desde el navegador de la app móvil.
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      } catch {
        // sessionStorage puede fallar en navegadores con storage
        // deshabilitado (modo incógnito muy restrictivo, etc.). No
        // bloqueamos el flow por eso.
      }
    }
  }

  if (!nativeApp && executionId && !hasRedirectUri) {
    // No lo hemos encontrado por querystring y estamos en el mismo flow
    // de ejecución que el último que sí lo detectó. Recuperamos.
    try {
      const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (raw) {
        const stored = JSON.parse(raw) as SessionStorageNativeApp;
        if (stored.executionId === executionId && isValid(stored.nativeApp)) {
          nativeApp = stored.nativeApp;
        }
      }
    } catch {
      // Mismo motivo que arriba.
    }
  }

  if (nativeApp && executionId) {
    // Persistimos para resistir un reload sin querystring dentro del
    // mismo flow (p.ej. usuario teclea password mal → KC recarga la
    // pantalla y perdemos el ?nativeApp=).
    try {
      sessionStorage.setItem(
        SESSION_STORAGE_KEY,
        JSON.stringify({ nativeApp, executionId } satisfies SessionStorageNativeApp),
      );
    } catch {
      // Idem.
    }
  }

  return nativeApp;
}

// Añade ?nativeApp=<valor> o &nativeApp=<valor> (según corresponda) a
// una URL si hay nativeApp activo. Mantenerlo en URLs salientes asegura
// que el siguiente paso del flow (resetCredentialsUrl, registrationUrl,
// loginUrl al "volver al inicio") sigue identificándose como app nativa.
export function getFinalURL(url: string, nativeApp: string | null): string {
  if (!nativeApp) return url;
  const sep = url.indexOf("?") === -1 ? "?" : "&";
  return `${url}${sep}nativeApp=${nativeApp}`;
}
